import { Activity, EntityEnum, listActivity, ListActivityAPIQueryParams } from '@goldcast/api/backend';
import {
  getVideoTypeFromActivityEntity,
  updateVideoToSpeakerDetailsStore,
  VideoToSpeakerDetails
} from './videoSpeakerDetailsStore';
import createStore from '@/stores/store';
import { isEntitySharedVideo } from '@/libs/activityUtils';
import { mergeWindows, subtractRanges } from '@/components/molecules/Transcript/EditTranscriptMenu/utils';

export type AlignedRecordings = Pick<
  Activity,
  'id' | 'entity' | 'transcript_mapping' | 'stage_time_spans' | 'user' | 'thumbnail' | 'source' | 'video_metadata'
> & {
  mp4_url: string;
  preview_url?: string;
  preview_audio_url?: string;
  associatedCloudRecordingId?: string;
};

const SORT_ORDER_FOR_ENTITIES: { [key in EntityEnum]?: number } = {
  aligned_screenshare_feed: 0,
  aligned_video_feed: 1,
  aligned_user_feed: 2
};

export const setAlignedRecordings = (broadcastId: string, recordings: AlignedRecordings[]) => {
  alignedRecordings.set(prev => ({ ...prev, [broadcastId]: recordings }));
};

export const getAlignedRecordings = (
  broadcastId: string | null,
  snapshot?: ReturnType<typeof alignedRecordings.getSnapshot>
) => {
  if (!broadcastId) {
    return [];
  }
  const alignedRecordingsSnapshot = snapshot ?? alignedRecordings.getSnapshot();
  return alignedRecordingsSnapshot[broadcastId] ?? [];
};

export const alignedRecordings = createStore<{ [broadcastId: string]: AlignedRecordings[] }>({});

/**
 * Adjusts the stage time spans of non-shared-video recordings to exclude
 * any time periods where shared video recordings are active.
 *
 * This ensures that no other entities have overlapping stage time spans
 * with shared video content, effectively "subtracting" shared video ranges
 * from other recordings' ranges.
 *
 * @param {AlignedRecordings[]} recordings - The list of aligned recordings. Each recording contains:
 *  - `entity`: Identifier for the entity type.
 *  - `stage_time_spans`: An array of objects with `start_time` and `end_time` (in seconds).
 * @returns {AlignedRecordings[]} A new array of recordings where stage time spans
 * of non-shared-video entities are adjusted to exclude periods covered by shared video.
 *
 * @example
 * const adjusted = adjustStageTimeSpansDueToPresenceOfSharedVideo(recordings);
 * // All non-shared-video stage time spans will no longer overlap with shared video spans.
 */
const excludeSharedVideoTimeSpans = (recordings: AlignedRecordings[]): AlignedRecordings[] => {
  const sharedVideoRecordings = recordings.filter(recording => isEntitySharedVideo(recording.entity));
  const stageTimeSpansForSharedVideo = sharedVideoRecordings
    .map(
      recording => recording.stage_time_spans?.map(span => [span.start_time, span.end_time] as [number, number]) ?? []
    )
    .flat();
  const mergedSortedTimeSpansForSharedVideo = mergeWindows(stageTimeSpansForSharedVideo);
  return recordings.map(recording => {
    if (isEntitySharedVideo(recording.entity)) {
      return recording;
    }
    const stageTimeSpans = (recording.stage_time_spans?.map(span => [span.start_time, span.end_time]) ?? []) as [
      number,
      number
    ][];
    const mergedAndSortedStageTimeSpans = mergeWindows(stageTimeSpans);
    const adjustedStageTimeSpans = subtractRanges(mergedAndSortedStageTimeSpans, mergedSortedTimeSpansForSharedVideo);
    const finalStageTimeSpans = adjustedStageTimeSpans.map(span => ({ start_time: span[0], end_time: span[1] }));
    return {
      ...recording,
      stage_time_spans: finalStageTimeSpans
    };
  });
};

export const fetchAlignedRecordings = async ({
  broadcast_id,
  usePureCloudRecording,
  queryParams = {}
}: {
  broadcast_id: string;
  usePureCloudRecording?: boolean;
  queryParams?: ListActivityAPIQueryParams;
}) => {
  const entities = ['aligned_user_feed', 'aligned_screenshare_feed', 'aligned_video_feed'];

  const { results } = await listActivity({
    queryParams: {
      broadcast_id,
      // "as any" needs to be done because of no type support for arrays
      entity: entities as any,
      ...queryParams
    },
    queryParamsStringifyOptions: {
      arrayFormat: 'comma'
    }
  });

  if (!results) {
    setAlignedRecordings(broadcast_id, []);
    return;
  }

  const recordingIdToSpeakerKeyMap: VideoToSpeakerDetails = {};

  const userIdToCloudRecordingMap: Record<string, string> = {};
  const userIdToLocalRecordingMap: Record<string, string> = {};

  results.forEach(recording => {
    if (recording.source === 'local') {
      userIdToLocalRecordingMap[recording.user.id + recording.entity] = recording.id;
    } else {
      userIdToCloudRecordingMap[recording.user.id + recording.entity] = recording.id;
    }
  });

  const processedRecordings: AlignedRecordings[] = results
    // Sort screenshare recordings to the top
    .sort((a, b) => {
      const aIndex = SORT_ORDER_FOR_ENTITIES[a.entity] ?? 0;
      const bIndex = SORT_ORDER_FOR_ENTITIES[b.entity] ?? 0;
      return aIndex - bIndex;
    })
    .map(recording => {
      // Build speaker mapping during the same iteration
      // TODO: This needs to be fixed. Not changing now since unrelated to my changes.
      // @ts-ignore
      const speakerKey = recording.transcript_mapping?.speaker_labels?.[0];
      recordingIdToSpeakerKeyMap[recording.id] = {
        speakerKey,
        videoType: getVideoTypeFromActivityEntity(recording.entity)
      };

      if (typeof usePureCloudRecording === 'boolean') {
        if (!usePureCloudRecording && recording.source === 'cloud') {
          // if not using pure cloud then filter out cloud recordings
          return null;
        }

        if (usePureCloudRecording && recording.source === 'local') {
          // if using pure cloud then filter out local recordings
          return null;
        }
      } else {
        // if ff=off and local exists, then filter out cloud recording
        if (recording.source === 'cloud' && userIdToLocalRecordingMap[recording.user.id + recording.entity]) {
          return null;
        }
      }

      // Return transformed recording
      return {
        id: recording.id,
        entity: recording.entity,
        transcript_mapping: recording.transcript_mapping,
        stage_time_spans: recording.stage_time_spans,
        user: recording.user,
        mp4_url: (recording as any).mp4_url,
        preview_url: (recording as any).preview_url,
        preview_audio_url: (recording as any).preview_audio_url,
        thumbnail: recording.thumbnail,
        source: recording.source,
        video_metadata: recording.video_metadata,
        associatedCloudRecordingId:
          recording.source === 'local' ? userIdToCloudRecordingMap[recording.user.id + recording.entity] : undefined
      };
    })
    .filter(a => a !== null);

  const timeSpansAdjustedRecordings = excludeSharedVideoTimeSpans(processedRecordings);

  updateVideoToSpeakerDetailsStore({ broadcastId: broadcast_id, updatedSpeakerDetails: recordingIdToSpeakerKeyMap });
  setAlignedRecordings(broadcast_id, timeSpansAdjustedRecordings);
};
